import {useMutation} from "@tanstack/react-query";
import {doc, setDoc} from "firebase/firestore";
import type {ZodType, z} from "zod/v4";

import {BaseCollectionConfig} from "~/api/BaseCollectionConfig";
import {firestore} from "~/firebase/firebase";

import {removeUndefined} from "../deepUndefinedRemover";
import {queryClient} from "../queryClient";

type MutationArgs<
  T extends ZodType,
  CollectionArgs extends NonNullable<unknown>,
> = {
  key: CollectionArgs;
  data: z.infer<T> & {id?: string};
};
export const mutationHookFactory = <
  T extends ZodType,
  CollectionArgs extends NonNullable<unknown>,
>(
  config: BaseCollectionConfig<T, CollectionArgs>,
) => {
  return () => {
    return useMutation<
      void,
      Error,
      MutationArgs<T, CollectionArgs & {id?: string}>
    >({
      mutationFn: async ({key, data}) => {
        // This function actually performs the mutation
        try {
          const parsed = config.parseData(data.id, data);
          const {id, ...mapped} = config.mapForPersistence(parsed);
          const documentRef = doc(firestore, ...config.getDocumentPath(key));
          await setDoc(documentRef, removeUndefined(mapped), {
            merge: false,
          });
          queryClient.setQueryData(config.getDocumentPath(key), data);
        } catch (err) {
          console.error("Error updating document: ", err);
          throw err;
        }
      },
    });
  };
};
